import React from 'react';

interface TeamKnowledgeSharingMatrixChartProps {
  data: Array<{
    source_team: string;
    target_team: string;
    knowledge_flow: number;
    collaboration_frequency: number;
    shared_resources: number;
    cross_team_projects: number;
  }>;
  teams: string[];
  loading?: boolean;
}

const TeamKnowledgeSharingMatrixChart: React.FC<TeamKnowledgeSharingMatrixChartProps> = ({ data, teams, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Knowledge Sharing Matrix</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0 || !teams || teams.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Knowledge Sharing Matrix</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No knowledge sharing data available
        </div>
      </div>
    );
  }

  const getIntensityColor = (intensity: number) => {
    if (intensity >= 80) return 'bg-red-500';
    if (intensity >= 60) return 'bg-orange-500';
    if (intensity >= 40) return 'bg-yellow-500';
    if (intensity >= 20) return 'bg-green-500';
    return 'bg-gray-300';
  };

  const getIntensityLevel = (intensity: number) => {
    if (intensity >= 80) return 'Very High';
    if (intensity >= 60) return 'High';
    if (intensity >= 40) return 'Medium';
    if (intensity >= 20) return 'Low';
    return 'Minimal';
  };

  // Create matrix data
  const matrixData = teams.map(sourceTeam => {
    const row: any = { source_team: sourceTeam };
    teams.forEach(targetTeam => {
      if (sourceTeam === targetTeam) {
        row[targetTeam] = 100; // Self-reference (diagonal)
      } else {
        const sharingData = data.find(d => d.source_team === sourceTeam && d.target_team === targetTeam);
        row[targetTeam] = sharingData ? sharingData.knowledge_flow : 0;
      }
    });
    return row;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Knowledge Sharing Matrix</h3>
      
      {/* Matrix Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2 text-xs font-medium text-gray-700 bg-gray-50"></th>
              {teams.map((team, index) => (
                <th key={`${team}-header-${index}`} className="border border-gray-300 p-2 text-xs font-medium text-gray-700 bg-gray-50 text-center">
                  {team.length > 8 ? team.substring(0, 8) + '...' : team}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixData.map((row, rowIndex) => (
              <tr key={`${row.source_team}-row-${rowIndex}`}>
                <td className="border border-gray-300 p-2 text-xs font-medium text-gray-700 bg-gray-50">
                  {row.source_team.length > 12 ? row.source_team.substring(0, 12) + '...' : row.source_team}
                </td>
                {teams.map((team, colIndex) => {
                  const intensity = row[team] as number;
                  const isDiagonal = rowIndex === colIndex;
                  return (
                    <td 
                      key={`${row.source_team}-${team}-${colIndex}`}
                      className={`border border-gray-300 p-2 text-center text-xs font-medium ${
                        isDiagonal 
                          ? 'bg-gray-200 text-gray-500' 
                          : intensity > 0 
                            ? 'text-white' 
                            : 'text-gray-500'
                      } ${!isDiagonal ? getIntensityColor(intensity) : ''}`}
                      title={`${row.source_team} → ${team}: ${intensity.toFixed(1)}% knowledge flow`}
                    >
                      {isDiagonal ? '—' : intensity.toFixed(0)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend and Insights */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Intensity Legend</h4>
          <div className="space-y-2">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Very High (80-100%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-orange-500 rounded mr-2"></div>
              <span className="text-sm text-gray-600">High (60-79%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Medium (40-59%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Low (20-39%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-300 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Minimal (0-19%)</span>
            </div>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Knowledge Sharing Insights</h4>
          <div className="space-y-3">
            {data
              .filter(d => d.knowledge_flow > 0)
              .sort((a, b) => b.knowledge_flow - a.knowledge_flow)
              .slice(0, 3)
              .map((item, index) => (
                <div key={`${item.source_team}-${item.target_team}`} className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-blue-600">
                      {item.source_team} → {item.target_team}
                    </span>
                    <span className="font-bold text-blue-600">
                      {item.knowledge_flow.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-xs text-blue-500 mt-1">
                    {getIntensityLevel(item.knowledge_flow)} knowledge flow
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamKnowledgeSharingMatrixChart;

